"use client";

import React, { useState } from "react";
import { ShieldCheck, ArrowRight, FileText, Layers, Microscope, Info, SlidersHorizontal, ChevronRight, CircleCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import InlineCta from "@/components/common/InlineCta";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface LocalizedItem {
  en: string;
  zh: string;
  es: string;
  de: string;
  fr: string;
  pt: string;
}
interface AlloySpec {
  id: string;
  grade: string;
  unsNumber: string;
  category: "Austenitic" | "High-Alloy" | "Duplex" | "Superalloy";
  categoryI18n: LocalizedItem;
  prenScore: number;
  corrosionRating: "Good" | "High" | "Exceptional" | "Extreme";
  corrosionRatingI18n: LocalizedItem;
  corrosionLevel: number; // 1 to 4
  keyFeatures: LocalizedItem[];
  composition: {
    element: string;
    percentage: string;
  }[];
  applications: LocalizedItem[];
  recommendedWeaves: LocalizedItem[];
  acidResistance: "Moderate" | "High" | "Superior" | "Immune";
  acidResistanceI18n: LocalizedItem;
  chloridePitting: "Standard" | "High" | "Exceptional" | "Extreme";
  chloridePittingI18n: LocalizedItem;
  costProfile: "Standard ($)" | "Moderate ($$)" | "Premium ($$$)" | "High-Spec ($$$$)";
  costProfileI18n: LocalizedItem;
  popularSpecs: string;
}
const ALLOY_DATA: AlloySpec[] = [{
  id: "ss304",
  grade: "AISI 304 / 304L",
  unsNumber: "S30400 / S30403",
  category: "Austenitic",
  categoryI18n: {
    en: "Austenitic",
    zh: "奥氏体",
    es: "Austenítico",
    de: "Austenitisch",
    fr: "Austénitique",
    pt: "Austenítico"
  },
  prenScore: 19,
  corrosionRating: "Good",
  corrosionRatingI18n: {
    en: "Good",
    zh: "良好",
    es: "Buena",
    de: "Gut",
    fr: "Bonne",
    pt: "Boa"
  },
  corrosionLevel: 1,
  keyFeatures: [{
    en: "Standard 18/8 chromium-nickel austenitic alloy",
    zh: "标准 18/8 铬镍奥氏体合金",
    es: "Aleación austenítica estándar de cromo-níquel 18/8",
    de: "Standard 18/8 Chrom-Nickel-Austenit-Legierung",
    fr: "Alliage austénitique chrome-nickel 18/8 standard",
    pt: "Liga austenítica padrão de cromo-níquel 18/8"
  }, {
    en: "Excellent cold-forming & drawing capability",
    zh: "优异的冷成型与拉拔加工性能",
    es: "Excelente conformabilidad en frío y capacidad de trefilado",
    de: "Hervorragende Kaltumform- und Drahtzieheigenschaften",
    fr: "Excellente formabilité à froid et tréfilabilité",
    pt: "Excelente conformabilidade a frio e trefilabilidade"
  }, {
    en: "Low carbon (304L) prevents carbide precipitation during welding",
    zh: "低碳 (304L) 抑制焊接时碳化物析出",
    es: "Bajo contenido de carbono (304L) que previene la precipitación de carburos en soldadura",
    de: "Niedriger Kohlenstoffgehalt (304L) verhindert Karbidausscheidung beim Schweißen",
    fr: "Faible teneur en carbone (304L) évitant la précipitation de carbures au soudage",
    pt: "Baixo teor de carbono (304L) evita precipitação de carbetos na soldagem"
  }, {
    en: "Cost-effective baseline for industrial liquid/air filtration",
    zh: "工业液体与气体过滤的高性价比基础方案",
    es: "Línea de base económica para filtración industrial de líquidos y aire",
    de: "Kosteneffiziente Basislegierung für industrielle Flüssigkeits- und Gasfiltration",
    fr: "Base économique pour la filtration industrielle liquide/air",
    pt: "Base econômica para filtração industrial de líquidos e ar"
  }],
  composition: [{
    element: "Cr",
    percentage: "18.0–20.0%"
  }, {
    element: "Ni",
    percentage: "8.0–10.5%"
  }, {
    element: "C",
    percentage: "≤ 0.03% (304L)"
  }, {
    element: "Fe",
    percentage: "Balance"
  }],
  applications: [{
    en: "Food & beverage strainers",
    zh: "食品与饮料过滤网",
    es: "Filtros de retención para alimentos y bebidas",
    de: "Siebe für die Lebensmittel- und Getränkeindustrie",
    fr: "Tamis agroalimentaires",
    pt: "Filtros para alimentos e bebidas"
  }, {
    en: "General water filtration",
    zh: "通用工业水处理过滤",
    es: "Filtración general de agua",
    de: "Allgemeine Wasserfiltration",
    fr: "Filtration d'eau générale",
    pt: "Filtração geral de água"
  }, {
    en: "Architectural mesh panels",
    zh: "建筑装饰金属网板",
    es: "Paneles de malla arquitectónica",
    de: "Architektur-Gewebeplatten",
    fr: "Panneaux de toile architecturale",
    pt: "Painéis de malha arquitetônica"
  }, {
    en: "Hydraulic fluid filters",
    zh: "液压油过滤网",
    es: "Filtros de fluido hidráulico",
    de: "Hydraulikflüssigkeitsfilter",
    fr: "Filtres pour fluides hydrauliques",
    pt: "Filtros de fluido hidráulico"
  }],
  recommendedWeaves: [{
    en: "Plain Weave (10–500 Mesh)",
    zh: "平织 (10–500 目)",
    es: "Tejido plano (10–500 mesh)",
    de: "Leinwandbindung (10–500 Mesh)",
    fr: "Tissage uni (10–500 mesh)",
    pt: "Trama lisa (10–500 mesh)"
  }, {
    en: "Twilled Weave",
    zh: "斜织",
    es: "Tejido asargado",
    de: "Köperbindung",
    fr: "Tissage croisé",
    pt: "Trama cruzada"
  }, {
    en: "Dutch Weave 24/110",
    zh: "荷兰编织 24/110",
    es: "Tejido holandés 24/110",
    de: "Tressengewebe 24/110",
    fr: "Tissage hollandais 24/110",
    pt: "Trama holandesa 24/110"
  }],
  acidResistance: "Moderate",
  acidResistanceI18n: {
    en: "Moderate",
    zh: "中等",
    es: "Moderada",
    de: "Mäßig",
    fr: "Modérée",
    pt: "Moderada"
  },
  chloridePitting: "Standard",
  chloridePittingI18n: {
    en: "Standard",
    zh: "标准",
    es: "Estándar",
    de: "Standard",
    fr: "Standard",
    pt: "Padrão"
  },
  costProfile: "Standard ($)",
  costProfileI18n: {
    en: "Standard ($)",
    zh: "标准 ($)",
    es: "Estándar ($)",
    de: "Standard ($)",
    fr: "Standard ($)",
    pt: "Padrão ($)"
  },
  popularSpecs: "100 Mesh 0.10mm Wire, 200 Mesh 0.05mm Wire"
}, {
  id: "ss316",
  grade: "AISI 316 / 316L",
  unsNumber: "S31600 / S31603",
  category: "Austenitic",
  categoryI18n: {
    en: "Austenitic",
    zh: "奥氏体",
    es: "Austenítico",
    de: "Austenitisch",
    fr: "Austénitique",
    pt: "Austenítico"
  },
  prenScore: 25,
  corrosionRating: "High",
  corrosionRatingI18n: {
    en: "High",
    zh: "高",
    es: "Alta",
    de: "Hoch",
    fr: "Élevée",
    pt: "Alta"
  },
  corrosionLevel: 2,
  keyFeatures: [{
    en: "2.0–3.0% Molybdenum addition for pitting resistance",
    zh: "添加 2.0–3.0% 钼元素增强耐点蚀性能",
    es: "Adición de 2.0–3.0% de molibdeno para resistencia a picaduras",
    de: "2,0–3,0 % Molybdänzusatz für Lochfraßbeständigkeit",
    fr: "Ajout de 2,0–3,0 % de molybdène pour la résistance aux piqûres",
    pt: "Adição de 2,0–3,0% de molibdênio para resistência a pites"
  }, {
    en: "Superior resistance to industrial chemicals & chlorides",
    zh: "对工业化学介质与氯化物具备优良耐受力",
    es: "Resistencia superior a productos químicos industriales y cloruros",
    de: "Hervorragende Beständigkeit gegen Industriechemikalien und Chloride",
    fr: "Résistance supérieure aux produits chimiques industriels et chlorures",
    pt: "Resistência superior a produtos químicos industriais e cloretos"
  }, {
    en: "316L low-carbon grade for optimal weld-joint durability",
    zh: "316L 超低碳牌号优化焊接接头耐久性",
    es: "Grado de bajo carbono 316L para óptima durabilidad en uniones soldadas",
    de: "Kohlenstoffarme Güte 316L für optimale Schweißnahtbeständigkeit",
    fr: "Nuance bas carbone 316L pour une durabilité maximale des soudures",
    pt: "Grau com baixo carbono 316L para durabilidade ideal em soldas"
  }, {
    en: "Industry standard for pharmaceutical & food-grade compliance",
    zh: "制药与食品卫生级应用的通用标准合金",
    es: "Estándar industrial para cumplimiento farmacéutico y alimentario",
    de: "Industriestandard für pharmazeutische und lebensmitteltaugliche Anwendungen",
    fr: "Standard industriel pour les exigences pharmaceutiques et alimentaires",
    pt: "Padrão industrial para conformidade farmacêutica e alimentícia"
  }],
  composition: [{
    element: "Cr",
    percentage: "16.0–18.0%"
  }, {
    element: "Ni",
    percentage: "10.0–14.0%"
  }, {
    element: "Mo",
    percentage: "2.0–3.0%"
  }, {
    element: "Fe",
    percentage: "Balance"
  }],
  applications: [{
    en: "Pharmaceutical batch filtration",
    zh: "制药批次精密过滤",
    es: "Filtración por lotes en farmacéutica",
    de: "Pharmazeutische Chargenfiltration",
    fr: "Filtration discontinue pharmaceutique",
    pt: "Filtração de lotes farmacêuticos"
  }, {
    en: "Marine sea-water strainers",
    zh: "海洋与海水过滤器",
    es: "Filtros de toma de agua de mar",
    de: "Meerwasser-Filter und -Siebe",
    fr: "Filtres marins pour eau de mer",
    pt: "Filtros para água do mar marinha"
  }, {
    en: "Polymer melt extrusion mesh",
    zh: "聚合物熔体挤出滤网",
    es: "Mallas de extrusión de polímeros fundidos",
    de: "Polymerschmelze-Extrusionssiebe",
    fr: "Toiles d'extrusion de polymères fondus",
    pt: "Malhas para extrusão de polímeros fundidos"
  }, {
    en: "Chemical liquid processing",
    zh: "化工液体流程过滤",
    es: "Procesamiento de líquidos químicos",
    de: "Chemische Flüssigprozessierung",
    fr: "Traitement de liquides chimiques",
    pt: "Processamento de líquidos químicos"
  }],
  recommendedWeaves: [{
    en: "Twilled Weave (200–635 Mesh)",
    zh: "斜织 (200–635 目)",
    es: "Tejido asargado (200–635 mesh)",
    de: "Köperbindung (200–635 Mesh)",
    fr: "Tissage croisé (200–635 mesh)",
    pt: "Trama cruzada (200–635 mesh)"
  }, {
    en: "Reverse Dutch Weave",
    zh: "反向荷兰编织",
    es: "Tejido holandés inverso",
    de: "Umgekehrte Tresse (Panzertresse)",
    fr: "Tissage hollandais inversé",
    pt: "Trama holandesa invertida"
  }, {
    en: "5-Layer Sintered",
    zh: "五层烧结网",
    es: "Malla sinterizada de 5 capas",
    de: "5-lagiges Sintergewebe",
    fr: "Toile frittée 5 couches",
    pt: "Malha sinterizada de 5 camadas"
  }],
  acidResistance: "High",
  acidResistanceI18n: {
    en: "High",
    zh: "高",
    es: "Alta",
    de: "Hoch",
    fr: "Élevée",
    pt: "Alta"
  },
  chloridePitting: "High",
  chloridePittingI18n: {
    en: "High",
    zh: "高",
    es: "Alta",
    de: "Hoch",
    fr: "Élevée",
    pt: "Alta"
  },
  costProfile: "Moderate ($$)",
  costProfileI18n: {
    en: "Moderate ($$)",
    zh: "中等 ($$)",
    es: "Moderado ($$)",
    de: "Moderat ($$)",
    fr: "Modéré ($$)",
    pt: "Moderado ($$)"
  },
  popularSpecs: "325 Mesh 0.035mm Wire, 24/110 Plain Dutch"
}, {
  id: "ss904l",
  grade: "AISI 904L",
  unsNumber: "N08904",
  category: "High-Alloy",
  categoryI18n: {
    en: "High-Alloy",
    zh: "高合金",
    es: "Alta aleación",
    de: "Hochlegiert",
    fr: "Haut alliage",
    pt: "Alta liga"
  },
  prenScore: 35,
  corrosionRating: "Exceptional",
  corrosionRatingI18n: {
    en: "Exceptional",
    zh: "极优",
    es: "Excepcional",
    de: "Außergewöhnlich",
    fr: "Exceptionnelle",
    pt: "Excepcional"
  },
  corrosionLevel: 3,
  keyFeatures: [{
    en: "High nickel (25%) and molybdenum (4.5%) with added copper",
    zh: "高镍 (25%) 与钼 (4.5%) 并添加铜元素",
    es: "Alto contenido de níquel (25%) y molibdeno (4.5%) con adición de cobre",
    de: "Hoher Nickel- (25 %) und Molybdängehalt (4,5 %) mit Kupferzusatz",
    fr: "Teneur élevée en nickel (25 %) et molybdène (4,5 %) avec cuivre ajouté",
    pt: "Alto teor de níquel (25%) e molibdênio (4,5%) com adição de cobre"
  }, {
    en: "Exceptional resistance to warm sulfuric & phosphoric acids",
    zh: "对温热硫酸和磷酸具有出色的抗腐蚀性",
    es: "Resistencia excepcional a ácidos sulfúrico y fosfórico calientes",
    de: "Hervorragende Beständigkeit gegen warme Schwefel- und Phosphorsäure",
    fr: "Résistance exceptionnelle aux acides sulfurique et phosphorique chauds",
    pt: "Resistência excepcional a ácidos sulfúrico e fosfórico mornos"
  }, {
    en: "Virtually immune to stress corrosion cracking in severe media",
    zh: "在恶劣介质中几乎完全免于应力腐蚀开裂",
    es: "Prácticamente inmune al agrietamiento por corrosión bajo tensión en medios severos",
    de: "Nahezu immun gegen Spannungsrisskorrosion in aggressiven Medien",
    fr: "Pratiquement insensible à la corrosion sous contrainte en milieux sévères",
    pt: "Praticamente imune à corrosão sob tensão em meios severos"
  }, {
    en: "Designed specifically for aggressive severe-service chemicals",
    zh: "专为严苛化工重腐蚀工况设计",
    es: "Diseñado específicamente para productos químicos agresivos en servicio severo",
    de: "Speziell für hochaggressive Chemikalien im Schwerlasteinsatz entwickelt",
    fr: "Spécifiquement conçu pour les produits chimiques agressifs en service sévère",
    pt: "Projetado especificamente para produtos químicos agressivos em serviço severo"
  }],
  composition: [{
    element: "Cr",
    percentage: "19.0–23.0%"
  }, {
    element: "Ni",
    percentage: "23.0–28.0%"
  }, {
    element: "Mo",
    percentage: "4.0–5.0%"
  }, {
    element: "Cu",
    percentage: "1.2–2.0%"
  }],
  applications: [{
    en: "Phosphoric acid production",
    zh: "磷酸生产工艺过滤",
    es: "Producción de ácido fosfórico",
    de: "Phosphorsäureproduktion",
    fr: "Production d'acide phosphorique",
    pt: "Produção de ácido fosfórico"
  }, {
    en: "Flue gas desulfurization (FGD)",
    zh: "烟气脱硫系统 (FGD)",
    es: "Desulfuración de gases de combustión (FGD)",
    de: "Rauchgasentschwefelung (REA)",
    fr: "Désulfuration des gaz de combustion (FGD)",
    pt: "Dessulfurização de gases de combustão (FGD)"
  }, {
    en: "Seawater cooling systems",
    zh: "海水冷却循环系统",
    es: "Sistemas de enfriamiento por agua de mar",
    de: "Meerwasser-Kühlsysteme",
    fr: "Systèmes de refroidissement à l'eau de mer",
    pt: "Sistemas de resfriamento com água do mar"
  }, {
    en: "Sulfuric acid strainers",
    zh: "浓硫酸过滤器",
    es: "Filtros de ácido sulfúrico",
    de: "Schwefelsäure-Filterelemente",
    fr: "Filtres pour acide sulfurique",
    pt: "Filtros de ácido sulfúrico"
  }],
  recommendedWeaves: [{
    en: "5-Layer Sintered Mesh",
    zh: "五层烧结网",
    es: "Malla sinterizada de 5 capas",
    de: "5-lagiges Sintergewebe",
    fr: "Toile frittée 5 couches",
    pt: "Malha sinterizada de 5 camadas"
  }, {
    en: "Heavy-Duty Dutch Weave",
    zh: "重型荷兰编织",
    es: "Tejido holandés de alta resistencia",
    de: "Schwere Tressengewebe",
    fr: "Tissage hollandais renforcé",
    pt: "Trama holandesa para serviços pesados"
  }, {
    en: "Screen Discs",
    zh: "过滤网片",
    es: "Discos filtrantes",
    de: "Filterscheiben (Ronden)",
    fr: "Disques de filtration",
    pt: "Discos de filtração"
  }],
  acidResistance: "Superior",
  acidResistanceI18n: {
    en: "Superior",
    zh: "卓越",
    es: "Superior",
    de: "Überlegen",
    fr: "Supérieure",
    pt: "Superior"
  },
  chloridePitting: "Exceptional",
  chloridePittingI18n: {
    en: "Exceptional",
    zh: "极优",
    es: "Excepcional",
    de: "Außergewöhnlich",
    fr: "Exceptionnelle",
    pt: "Excepcional"
  },
  costProfile: "Premium ($$$)",
  costProfileI18n: {
    en: "Premium ($$$)",
    zh: "高端 ($$$)",
    es: "Premium ($$$)",
    de: "Premium ($$$)",
    fr: "Premium ($$$)",
    pt: "Premium ($$$)"
  },
  popularSpecs: "Custom Multilayer Sintered Laminates"
}, {
  id: "duplex2205",
  grade: "Duplex 2205",
  unsNumber: "S32205 / S31803",
  category: "Duplex",
  categoryI18n: {
    en: "Duplex",
    zh: "双相钢",
    es: "Dúplex",
    de: "Duplex",
    fr: "Duplex",
    pt: "Duplex"
  },
  prenScore: 36,
  corrosionRating: "Exceptional",
  corrosionRatingI18n: {
    en: "Exceptional",
    zh: "极优",
    es: "Excepcional",
    de: "Außergewöhnlich",
    fr: "Exceptionnelle",
    pt: "Excepcional"
  },
  corrosionLevel: 3,
  keyFeatures: [{
    en: "50/50 Ferritic-Austenitic dual-phase microstructure",
    zh: "50/50 铁素体-奥氏体双相显微组织",
    es: "Microestructura bifásica ferrítico-austenítica 50/50",
    de: "50/50 ferritisch-austenitisches Duplex-Gefüge",
    fr: "Microstructure biphasée ferritique-austénitique 50/50",
    pt: "Microestrutura bifásica ferrítico-austenítica 50/50"
  }, {
    en: "Double the yield strength of conventional 316L stainless",
    zh: "屈服强度达到传统 316L 不锈钢的两倍",
    es: "Duplica el límite elástico del acero inoxidable 316L convencional",
    de: "Doppelte Streckgrenze im Vergleich zu Standard-316L",
    fr: "Limite d'élasticité double de celle de l'inox 316L standard",
    pt: "O dobro da tensão de escoamento do inox 316L convencional"
  }, {
    en: "Outstanding resistance to chloride stress corrosion cracking",
    zh: "卓越的耐氯化物应力腐蚀开裂能力",
    es: "Resistencia extraordinaria al agrietamiento por corrosión bajo tensión por cloruros",
    de: "Hervorragende Beständigkeit gegen chloridinduzierte Spannungsrisskorrosion",
    fr: "Résistance remarquable à la corrosion sous contrainte en milieu chloruré",
    pt: "Excelente resistência à corrosão sob tensão induzida por cloretos"
  }, {
    en: "High mechanical erosion & abrasion resistance",
    zh: "高机械抗冲蚀与抗磨损特性",
    es: "Alta resistencia mecánica a la erosión y abrasión",
    de: "Hohe mechanische Erosions- und Abrasionsbeständigkeit",
    fr: "Forte résistance mécanique à l'érosion et à l'abrasion",
    pt: "Alta resistência mecânica à erosão e à abrasão"
  }],
  composition: [{
    element: "Cr",
    percentage: "22.0–23.0%"
  }, {
    element: "Ni",
    percentage: "4.5–6.5%"
  }, {
    element: "Mo",
    percentage: "3.0–3.5%"
  }, {
    element: "N",
    percentage: "0.14–0.20%"
  }],
  applications: [{
    en: "Offshore oil & gas separation",
    zh: "海上油气开采分离",
    es: "Separación offshore de petróleo y gas",
    de: "Offshore-Öl- und Gastrennung",
    fr: "Séparation pétrole et gaz offshore",
    pt: "Separação offshore de petróleo e gás"
  }, {
    en: "Pulp & paper bleaching filters",
    zh: "制浆造纸漂白滤网",
    es: "Filtros para blanqueo de pulpa y papel",
    de: "Bleichfilter für die Zellstoff- und Papierindustrie",
    fr: "Filtres de blanchiment pâte et papier",
    pt: "Filtros para branqueamento de celulose e papel"
  }, {
    en: "High-pressure hydraulic strainers",
    zh: "高压液压过滤器",
    es: "Filtros hidráulicos de alta presión",
    de: "Hochdruck-Hydrauliksiebe",
    fr: "Tamis hydrauliques haute pression",
    pt: "Filtros hidráulicos de alta pressão"
  }, {
    en: "Desalination intake screens",
    zh: "海水淡化进水口格栅",
    es: "Rejillas de captación para desalinización",
    de: "Einlassgitter für Meerwasserentsalzungsanlagen",
    fr: "Grilles de prise d'eau de dessalement",
    pt: "Telas de captação para dessalinização"
  }],
  recommendedWeaves: [{
    en: "Wedge Wire Screen",
    zh: "楔形丝滤网",
    es: "Tamiz de alambre en cuña",
    de: "Spaltsiebe (Keilspaltsiebe)",
    fr: "Crépine à fil profilé (wedge wire)",
    pt: "Tela de arame em cunha"
  }, {
    en: "Heavy Plain Weave",
    zh: "重型平织网",
    es: "Tejido plano pesado",
    de: "Schweres Leinwandgewebe",
    fr: "Tissage uni lourd",
    pt: "Trama lisa pesada"
  }, {
    en: "V-Wire Cylinders",
    zh: "V型丝滤筒",
    es: "Cilindros de alambre en V",
    de: "V-Draht-Spaltzylinder",
    fr: "Cylindres à fil en V",
    pt: "Cilindros de arame em V"
  }],
  acidResistance: "High",
  acidResistanceI18n: {
    en: "High",
    zh: "高",
    es: "Alta",
    de: "Hoch",
    fr: "Élevée",
    pt: "Alta"
  },
  chloridePitting: "Exceptional",
  chloridePittingI18n: {
    en: "Exceptional",
    zh: "极优",
    es: "Excepcional",
    de: "Außergewöhnlich",
    fr: "Exceptionnelle",
    pt: "Excepcional"
  },
  costProfile: "Premium ($$$)",
  costProfileI18n: {
    en: "Premium ($$$)",
    zh: "高端 ($$$)",
    es: "Premium ($$$)",
    de: "Premium ($$$)",
    fr: "Premium ($$$)",
    pt: "Premium ($$$)"
  },
  popularSpecs: "Slot 0.1mm Wedge Wire, Heavy Gauge Square Mesh"
}, {
  id: "hastelloyc276",
  grade: "Hastelloy C-276 / Inconel 600",
  unsNumber: "N10276 / N06600",
  category: "Superalloy",
  categoryI18n: {
    en: "Superalloy",
    zh: "超合金",
    es: "Superaleación",
    de: "Superlegierung",
    fr: "Superalliage",
    pt: "Superliga"
  },
  prenScore: 68,
  corrosionRating: "Extreme",
  corrosionRatingI18n: {
    en: "Extreme",
    zh: "极致",
    es: "Extrema",
    de: "Extrem",
    fr: "Extrême",
    pt: "Extrema"
  },
  corrosionLevel: 4,
  keyFeatures: [{
    en: "Nickel-molybdenum-chromium superalloy with tungsten",
    zh: "含钨镍钼铬高温耐蚀超合金",
    es: "Superaleación de níquel-molibdeno-cromo con tungsteno",
    de: "Nickel-Molybdän-Chrom-Superlegierung mit Wolfram",
    fr: "Superalliage nickel-molybdène-chrome avec tungstène",
    pt: "Superliga de níquel-molibdênio-cromo com tungstênio"
  }, {
    en: "Unmatched resistance to wet chlorine gas, hypochlorites & chlorine dioxide",
    zh: "对湿氯气、次氯酸盐及二氧化氯具有无与伦比的耐腐蚀力",
    es: "Resistencia inigualable al cloro gas húmedo, hipocloritos y dióxido de cloro",
    de: "Unübertroffene Beständigkeit gegen feuchtes Chlorgas, Hypochlorite und Chlordioxid",
    fr: "Résistance inégalée au chlore gazeux humide, hypochlorites et dioxyde de chlore",
    pt: "Resistência incomparável a gás cloro úmido, hipocloritos e dióxido de cloro"
  }, {
    en: "Maximum performance in localized corrosion and severe oxidation",
    zh: "在局部腐蚀和剧烈氧化环境中保持卓越性能",
    es: "Máximo rendimiento contra la corrosión localizada y la oxidación severa",
    de: "Höchstleistung bei lokaler Korrosion und schwerer Oxidation",
    fr: "Performance maximale face à la corrosion localisée et à l'oxydation sévère",
    pt: "Desempenho máximo contra corrosão localizada e oxidação severa"
  }],
  composition: [{
    element: "Ni",
    percentage: "Balance (~57%)"
  }, {
    element: "Mo",
    percentage: "15.0–17.0%"
  }, {
    element: "Cr",
    percentage: "14.5–16.5%"
  }, {
    element: "W",
    percentage: "3.0–4.5%"
  }],
  applications: [{
    en: "Petrochemical catalyst recovery",
    zh: "石化催化剂回收拦截",
    es: "Recuperación de catalizadores petroquímicos",
    de: "Rückgewinnung petrochemischer Katalysatoren",
    fr: "Récupération de catalyseurs pétrochimiques",
    pt: "Recuperação de catalisadores petroquímicos"
  }, {
    en: "Waste incineration filtration",
    zh: "垃圾焚烧尾气净化过滤",
    es: "Filtración en incineración de residuos",
    de: "Filtration in Müllverbrennungsanlagen",
    fr: "Filtration pour incinération de déchets",
    pt: "Filtração para incineração de resíduos"
  }, {
    en: "Hot gas particulate separation",
    zh: "高温烟气粉尘颗粒分离",
    es: "Separación de partículas de gases calientes",
    de: "Heißgas-Partikelabscheidung",
    fr: "Séparation de particules de gaz chauds",
    pt: "Separação de partículas em gases quentes"
  }, {
    en: "Aerospace high-temp strainers",
    zh: "航空航天高温流体过滤器",
    es: "Filtros de alta temperatura para el sector aeroespacial",
    de: "Hochtemperaturfilter für die Luft- und Raumfahrt",
    fr: "Tamis haute température pour l'aérospatiale",
    pt: "Filtros de alta temperatura para o setor aeroespacial"
  }],
  recommendedWeaves: [{
    en: "Sintered Dutch Weave",
    zh: "荷兰烧结网",
    es: "Tejido holandés sinterizado",
    de: "Gesintertes Tressengewebe",
    fr: "Tissage hollandais fritté",
    pt: "Trama holandesa sinterizada"
  }, {
    en: "Custom Woven High-Temp Wire Mesh",
    zh: "定制耐高温编织金属丝网",
    es: "Malla de alambre tejida a medida para alta temperatura",
    de: "Kundenspezifisches Hochtemperatur-Drahtgewebe",
    fr: "Toile métallique tissée sur mesure pour haute température",
    pt: "Malha de arame tecida sob medida para alta temperatura"
  }],
  acidResistance: "Immune",
  acidResistanceI18n: {
    en: "Immune",
    zh: "完全免疫",
    es: "Inmune",
    de: "Immun",
    fr: "Immunisée",
    pt: "Imune"
  },
  chloridePitting: "Extreme",
  chloridePittingI18n: {
    en: "Extreme",
    zh: "极致",
    es: "Extrema",
    de: "Extrem",
    fr: "Extrême",
    pt: "Extrema"
  },
  costProfile: "High-Spec ($$$$)",
  costProfileI18n: {
    en: "High-Spec ($$$$)",
    zh: "特级 ($$$$)",
    es: "Alta gama ($$$$)",
    de: "Hochspezialisiert ($$$$)",
    fr: "Haute spécification ($$$$)",
    pt: "Alta especificação ($$$$)"
  },
  popularSpecs: "Custom Micron Sintered Mesh Packs"
}];
export default function AlloyGuide() {
  const {
    lang
  } = useLanguage();
  const copy = {
    isoBadge: "ISO 9044 & ASTM E2016 COMPLIANT",
    guideBadge: {
      en: "Corrosion-Resistant Alloys & Finishes",
      zh: "耐腐蚀合金与表面处理指南",
      es: "Guía de aleaciones y acabados resistentes a la corrosión",
      de: "Leitfaden für korrosionsbeständige Legierungen & Oberflächen",
      fr: "Guide des alliages et finitions résistants à la corrosion",
      pt: "Guia de ligas e acabamentos resistentes à corrosão"
    },
    h1: {
      en: "Stainless Steel Alloy Selection & Surface Finish Guide for Filtration Mesh",
      zh: "过滤丝网不锈钢合金选型与表面处理规格指南",
      es: "Guía de selección de aleaciones de acero inoxidable y acabados para malla filtrante",
      de: "Leitfaden zur Auswahl von Edelstahllegierungen und Oberflächen für Filtergewebe",
      fr: "Guide de sélection des alliages d'acier inoxydable et finitions pour toiles filtrantes",
      pt: "Guia de seleção de ligas de aço inoxidável e acabamentos para malha de filtração"
    },
    lead: {
      en: "Select the optimal alloy composition and surface condition engineered for specific chemical exposure, thermal threshold, and mechanical differential pressure loads. MutualFilter manufactures technical woven wire mesh and sintered porous media in standard austenitic and severe-service superalloys; EN 10204 3.1 mill certification is available when specified in the order.",
      zh: "针对化工酸碱介质、高温烟气净化及卫生级制药过滤要求，精选最优金属合金成分与表面状态。MutualFilter（河北安平）生产全系列 AISI 304、316L、904L、双相钢 2205 及 Hastelloy C-276 精密金属网，EN 10204 3.1 质检证明与出厂炉号溯源可按订单要求提供。",
      es: "Seleccione la composición de aleación y el estado superficial óptimos para exposiciones químicas específicas, umbrales térmicos y cargas de presión diferencial mecánica. MutualFilter fabrica malla de alambre tejida técnica y medios porosos sinterizados en aceros austeníticos estándar y superaleaciones para servicio severo; los certificados de fábrica EN 10204 3.1 están disponibles cuando se especifiquen en el pedido.",
      de: "Wählen Sie die optimale Legierungszusammensetzung und Oberflächenbeschaffenheit für spezifische chemische Belastungen, Temperaturgrenzen und mechanische Differenzdrücke. MutualFilter fertigt technisches Drahtgewebe und poröse Sintermedien in standardisierten austenitischen Güten sowie hochlegierten Superlegierungen; EN 10204 3.1 Werkszeugnisse sind auf Bestellung erhältlich.",
      fr: "Sélectionnez la composition d'alliage et l'état de surface optimaux conçus pour des expositions chimiques précises, des seuils thermiques et des contraintes de pression différentielle. MutualFilter produit de la toile métallique tissée technique et des médias poreux frittés en inox austénitique standard et superalliages pour conditions sévères ; le certificat usine EN 10204 3.1 est disponible sur demande lors de la commande.",
      pt: "Selecione a composição de liga e a condição superficial ideais projetadas para exposição química específica, limites térmicos e cargas de pressão diferencial mecânica. A MutualFilter fabrica malha de arame tecida técnica e meios porosos sinterizados em aços austeníticos padrão e superligas para serviços severos; o certificado de fábrica EN 10204 3.1 está disponível quando especificado no pedido."
    },
    ctaVerification: {
      en: "Material Verification Guide",
      zh: "材料正材验证规范",
      es: "Guía de verificación de materiales",
      de: "Leitfaden zur Materialprüfung",
      fr: "Guide de vérification des matériaux",
      pt: "Guia de verificação de materiais"
    },
    quickMatrixTitle: {
      en: "Alloy Metallurgy Quick Matrix",
      zh: "合金冶金选型速查表",
      es: "Matriz rápida de metalurgia de aleaciones",
      de: "Schnellmatrix für Legierungsmetallurgie",
      fr: "Matrice rapide de métallurgie des alliages",
      pt: "Matriz rápida de metalurgia de ligas"
    },
    stockCustom: {
      en: "Stock & Custom",
      zh: "现货与定织",
      es: "Stock y fabricación a medida",
      de: "Lager & Sonderanfertigung",
      fr: "Stock et sur mesure",
      pt: "Estoque e sob encomenda"
    },
    keyAlloys: {
      en: "Key Industrial Alloys",
      zh: "核心工业合金牌号",
      es: "Aleaciones industriales clave",
      de: "Wichtige Industrielegierungen",
      fr: "Alliages industriels majeurs",
      pt: "Principais ligas industriais"
    },
    corrosionMatrixLink: {
      en: "Corrosion Matrix",
      zh: "耐腐蚀矩阵表",
      es: "Matriz de corrosión",
      de: "Korrosionsmatrix",
      fr: "Matrice de corrosion",
      pt: "Matriz de corrosão"
    },
    traceabilityCardTitle: {
      en: "Batch Traceability & Verification",
      zh: "正材化验与材质追溯保障",
      es: "Trazabilidad de lote y garantía de material",
      de: "Chargenrückverfolgbarkeit & Materialnachweis",
      fr: "Traçabilité des lots et vérification matière",
      pt: "Rastreabilidade de lote e garantia de material"
    },
    traceabilityCardDesc: {
      en: "EN 10204 3.1 inspection certificates and XRF spectrometer testing are available when specified in the order for woven or sintered mesh lots.",
      zh: "EN 10204 3.1 材质检验报告 (MTR) 与便携式 XRF 光谱 PMI 元素数据可按订单要求提供，支持食品卫生、制药与高压化工应用。",
      es: "Los certificados de inspección EN 10204 3.1 y los análisis espectrométricos XRF están disponibles cuando se especifiquen en el pedido para lotes de malla tejida o sinterizada.",
      de: "EN 10204 3.1 Abnahmeprüfzeugnisse und RFA-Spektrometerprüfungen (PMI) sind auf Bestellung für gewebte oder gesinterte Chargen erhältlich.",
      fr: "Les certificats d'inspection EN 10204 3.1 et les contrôles par spectromètre XRF sont disponibles sur demande lors de la commande pour les lots de toiles tissées ou frittées.",
      pt: "Certificados de inspeção EN 10204 3.1 e testes por espectrômetro XRF estão disponíveis quando especificados no pedido para lotes de malha tecida ou sinterizada."
    },
    browseStandards: {
      en: "Browse Technical Standards & Tolerances",
      zh: "查阅技术资源与金属丝网公差标准",
      es: "Consultar normas técnicas y tolerancias",
      de: "Technische Normen & Toleranzen anzeigen",
      fr: "Consulter les normes techniques et tolérances",
      pt: "Consultar normas técnicas e tolerâncias"
    },
    sec2Badge: {
      en: "Metallurgical Reference",
      zh: "冶金化学成分与耐蚀评级",
      es: "Referencia metalúrgica",
      de: "Metallurgische Referenz",
      fr: "Référence métallurgique",
      pt: "Referência metalúrgica"
    },
    sec2Heading: {
      en: "Stainless steel alloy selection guide for filtration mesh",
      zh: "过滤丝网不锈钢合金详细对比表",
      es: "Guía de selección de aleaciones de acero inoxidable para malla filtrante",
      de: "Leitfaden zur Auswahl von Edelstahllegierungen für Filtergewebe",
      fr: "Guide de sélection des alliages d'acier inoxydable pour toiles de filtration",
      pt: "Guia de seleção de ligas de aço inoxidável para malha de filtração"
    },
    sec2Lead: {
      en: "Select the optimal alloy composition engineered for your specific chemical exposure, thermal threshold, and mechanical load requirements. MutualFilter manufactures technical mesh in standard and severe-service grades.",
      zh: "对比各奥氏体、高合金、双相钢与镍基超合金在酸洗、海水、氯离子点蚀环境及高温氧化工况下的综合力学表现与成分比例。",
      es: "Seleccione la composición de aleación óptima diseñada para sus requisitos específicos de exposición química, límite térmico y carga mecánica. MutualFilter fabrica mallas técnicas en grados estándar y de servicio severo.",
      de: "Wählen Sie die optimale Legierungszusammensetzung abgestimmt auf chemische Einflüsse, Temperaturbelastungen und mechanische Anforderungen. MutualFilter fertigt technisches Gewebe in Standard- und Schwerlastgüten.",
      fr: "Sélectionnez la composition d'alliage optimale conçue pour vos contraintes chimiques, thermiques et mécaniques. MutualFilter fabrique des toiles techniques en nuances standard et pour conditions sévères.",
      pt: "Selecione a composição de liga ideal projetada para suas necessidades específicas de exposição química, limite térmico e carga mecânica. A MutualFilter fabrica malhas técnicas em graus padrão e para serviços severos."
    },
    tabCards: {
      en: "Detailed Specification Cards",
      zh: "详细合金规格卡片",
      es: "Tarjetas de especificación detalladas",
      de: "Detaillierte Spezifikationskarten",
      fr: "Fiches de spécifications détaillées",
      pt: "Fichas de especificação detalhadas"
    },
    tabMatrix: {
      en: "Comparison Matrix Table",
      zh: "性能对比矩阵表",
      es: "Tabla matricial comparativa",
      de: "Vergleichsmatrix-Tabelle",
      fr: "Tableau comparatif matriciel",
      pt: "Tabela comparativa matricial"
    },
    categoryFilterLabel: {
      en: "Category:",
      zh: "合金分类:",
      es: "Categoría:",
      de: "Kategorie:",
      fr: "Catégorie :",
      pt: "Categoria:"
    },
    corrosionRatingLabel: {
      en: "Corrosion Rating",
      zh: "耐腐蚀评级",
      es: "Clasificación anticorrosión",
      de: "Korrosionseinstufung",
      fr: "Indice de corrosion",
      pt: "Classificação de corrosão"
    },
    keyTraitsTitle: {
      en: "Key Metallurgical Traits",
      zh: "核心金相特征",
      es: "Propiedades metalúrgicas clave",
      de: "Wesentliche metallurgische Eigenschaften",
      fr: "Propriétés métallurgiques clés",
      pt: "Propriedades metalúrgicas essenciais"
    },
    nominalCompTitle: {
      en: "Nominal Chemical Composition (%):",
      zh: "标称化学成分 (%):",
      es: "Composición química nominal (%):",
      de: "Chemische Richtanalyse (%):",
      fr: "Composition chimique nominale (%):",
      pt: "Composição química nominal (%):"
    },
    recommendedWeavesTitle: {
      en: "Recommended Mesh Structures:",
      zh: "推荐编织形式:",
      es: "Estructuras de malla recomendadas:",
      de: "Empfohlene Gewebestrukturen:",
      fr: "Structures de toiles recommandées :",
      pt: "Estruturas de malha recomendadas:"
    },
    applicationsTitle: {
      en: "Primary B2B Application Contexts:",
      zh: "主要工业应用领域:",
      es: "Contextos principales de aplicación B2B:",
      de: "Typische B2B-Anwendungsbereiche:",
      fr: "Principaux domaines d'application B2B :",
      pt: "Principais contextos de aplicação B2B:"
    },
    costIndexLabel: {
      en: "Cost Index:",
      zh: "成本参考:",
      es: "Índice de coste:",
      de: "Kostenindex:",
      fr: "Indice de coût :",
      pt: "Índice de custo:"
    },
    btnGradeMatrix: {
      en: "Grade Matrix",
      zh: "耐腐蚀矩阵",
      es: "Matriz de grados",
      de: "Güten-Matrix",
      fr: "Matrice de nuances",
      pt: "Matriz de graus"
    },
    btnInquireRfq: {
      en: "Inquire RFQ",
      zh: "索取报价",
      es: "Pedir cotización",
      de: "Anfrage senden",
      fr: "Demander un devis",
      pt: "Solicitar cotação"
    },
    prenTooltipTitle: {
      en: "Pitting Resistance Equivalent Number",
      zh: "耐点蚀当量数 (PREN)",
      es: "Número equivalente de resistencia a la picadura (PREN)",
      de: "Wirksumme / Lochfraßbeständigkeitszahl (PREN)",
      fr: "Indice de résistance aux piqûres (PREN)",
      pt: "Número de resistência equivalente a pites (PREN)"
    },
    prenTooltipDesc: {
      en: "PREN = %Cr + 3.3(%Mo) + 16(%N). Higher values indicate greater pitting corrosion resistance in chloride environments.",
      zh: "PREN = %Cr + 3.3(%Mo) + 16(%N)。数值越高，表示在含氯离子环境下的耐点蚀能力越强。",
      es: "PREN = %Cr + 3.3(%Mo) + 16(%N). Los valores más altos indican mayor resistencia a la corrosión por picaduras en ambientes con cloruros.",
      de: "PREN = %Cr + 3,3(%Mo) + 16(%N). Höhere Werte weisen auf eine größere Lochfraßbeständigkeit in chloridhaltigen Umgebungen hin.",
      fr: "PREN = %Cr + 3,3(%Mo) + 16(%N). Une valeur plus élevée indique une plus grande résistance à la corrosion par piqûres en milieu chloruré.",
      pt: "PREN = %Cr + 3,3(%Mo) + 16(%N). Valores mais altos indicam maior resistência à corrosão por pites em ambientes com cloretos."
    },
    matrixTableTitle: {
      en: "Comprehensive Alloy Performance Comparison",
      zh: "综合合金性能对比",
      es: "Comparativa integral de rendimiento de aleaciones",
      de: "Umfassender Leistungsvergleich der Legierungen",
      fr: "Comparaison globale des performances des alliages",
      pt: "Comparação abrangente de desempenho das ligas"
    },
    matrixTableSubtitle: {
      en: "Compare chemical resistance, thermal thresholds, and mechanical profile across grades.",
      zh: "对比各牌号的耐化学性、耐热阈值及力学特征。",
      es: "Compare la resistencia química, los umbrales térmicos y el perfil mecánico entre grados.",
      de: "Vergleichen Sie chemische Beständigkeit, Temperaturgrenzen und mechanische Eigenschaften.",
      fr: "Comparez la résistance chimique, les seuils thermiques et les profils mécaniques selon les nuances.",
      pt: "Compare resistência química, limites térmicos e perfil mecânico entre graus."
    },
    thAlloyGrade: {
      en: "Alloy Grade",
      zh: "合金牌号",
      es: "Grado de aleación",
      de: "Legierungsgüte",
      fr: "Nuance d'alliage",
      pt: "Grau de liga"
    },
    thUnsNo: {
      en: "UNS No.",
      zh: "UNS 编号",
      es: "N.º UNS",
      de: "UNS-Nr.",
      fr: "N° UNS",
      pt: "Nº UNS"
    },
    thPren: {
      en: "PREN",
      zh: "PREN",
      es: "PREN",
      de: "PREN",
      fr: "PREN",
      pt: "PREN"
    },
    thAcidRes: {
      en: "Acid Resistance",
      zh: "耐酸性能",
      es: "Resistencia a ácidos",
      de: "Säurebeständigkeit",
      fr: "Résistance aux acides",
      pt: "Resistência a ácidos"
    },
    thChlorideRes: {
      en: "Chloride Resistance",
      zh: "耐氯化物性能",
      es: "Resistencia a cloruros",
      de: "Chloridbeständigkeit",
      fr: "Résistance aux chlorures",
      pt: "Resistência a cloretos"
    },
    thRelCost: {
      en: "Relative Cost",
      zh: "相对成本",
      es: "Coste relativo",
      de: "Relative Kosten",
      fr: "Coût relatif",
      pt: "Custo relativo"
    },
    thSpec: {
      en: "Specification",
      zh: "规格",
      es: "Especificación",
      de: "Spezifikation",
      fr: "Spécification",
      pt: "Especificação"
    },
    btnSpecs: {
      en: "Specs",
      zh: "规格",
      es: "Especificaciones",
      de: "Spezifikationen",
      fr: "Spécifications",
      pt: "Especificações"
    },
    tableFootInfo: {
      en: "Custom alloy weaves including Monel 400, Titanium Grade 2, and Incoloy 825 available on request.",
      zh: "可按要求定制 Monel 400、Titanium Grade 2 及 Incoloy 825 等特种合金编织网。",
      es: "Mallas en aleaciones especiales como Monel 400, Titanio Grado 2 e Incoloy 825 disponibles bajo pedido.",
      de: "Sonderlegierungs-Gewebe einschließlich Monel 400, Titan Grade 2 und Incoloy 825 auf Anfrage lieferbar.",
      fr: "Tissages en alliages spéciaux comprenant Monel 400, Titane Grade 2 et Incoloy 825 disponibles sur demande.",
      pt: "Telas em ligas especiais, incluindo Monel 400, Titânio Grau 2 e Incoloy 825, disponíveis sob consulta."
    },
    tableFootLink: {
      en: "View full corrosion resistance matrix",
      zh: "查看完整耐腐蚀矩阵表",
      es: "Ver la matriz completa de resistencia a la corrosión",
      de: "Vollständige Korrosionsbeständigkeits-Matrix anzeigen",
      fr: "Voir la matrice complète de résistance à la corrosion",
      pt: "Ver matriz completa de resistência à corrosão"
    },
    heroImageAlt: {
      en: "Cold-toned stainless steel wire mesh etched grain microstructure under metallurgical microscope",
      zh: "金相显微镜下经过化学腐蚀的冷轧不锈钢丝网金相显微晶粒组织",
      es: "Microestructura de grano grabado de malla de acero inoxidable bajo microscopio metalúrgico",
      de: "Geätztes Gefügebild von Edelstahldrahtgewebe unter dem Metallurgiemikroskop",
      fr: "Microstructure des grains d'une toile en acier inoxydable après attaque chimique sous microscope métallurgique",
      pt: "Microestrutura de grãos atacados de malha de aço inoxidável sob microscópio metalúrgico"
    },
    heroImageCaption: {
      en: "Austenitic Grain Boundary Structure (ASTM E112 / ISO 9044)",
      zh: "奥氏体晶界组织金相检验 (ASTM E112 / ISO 9044)",
      es: "Estructura de borde de grano austenítico (ASTM E112 / ISO 9044)",
      de: "Austenitische Korngrenzenstruktur (ASTM E112 / ISO 9044)",
      fr: "Structure des joints de grains austénitiques (ASTM E112 / ISO 9044)",
      pt: "Estrutura de contorno de grão austenítico (ASTM E112 / ISO 9044)"
    }
  };
  const engineeringChecklist = [{
    label: pick(lang, {
      en: "Standard 304/304L/316/316L woven wire cloth stock",
      zh: "标准与超低碳奥氏体不锈钢现货库存储备",
      es: "Stock de tela metálica tejida estándar 304/304L/316/316L",
      de: "Lagerbestand an Standard-Drahtgewebe aus 304/304L/316/316L",
      fr: "Stock de toiles métalliques tissées standard 304/304L/316/316L",
      pt: "Estoque de tela de arame tecida padrão 304/304L/316/316L"
    }),
    val: "AISI 304 / 316L"
  }, {
    label: pick(lang, {
      en: "Severe chemical acid & high-temperature alloys",
      zh: "双相钢与镍基超合金严苛强腐蚀环境定制",
      es: "Aleaciones para ácidos químicos severos y altas temperaturas",
      de: "Legierungen für aggressive Säuren & Hochtemperaturanwendungen",
      fr: "Alliages pour acides chimiques sévères et hautes températures",
      pt: "Ligas para ácidos químicos severos e altas temperaturas"
    }),
    val: "904L / Hastelloy"
  }, {
    label: pick(lang, {
      en: "Positive Material Identification & mill certificates",
      zh: "正材成分 PMI 光谱分析与 EN 10204 3.1 报告",
      es: "Identificación positiva de materiales y certificados de fábrica",
      de: "Positive Materialidentifikation (PMI) & Werkszeugnisse",
      fr: "Contrôle PMI de conformité matière et certificats usine",
      pt: "Identificação positiva de materiais e certificados de fábrica"
    }),
    val: "EN 10204 3.1 MTR"
  }, {
    label: pick(lang, {
      en: "Plain, Twill, Dutch weave & sintered laminates",
      zh: "方孔平织、斜织、荷兰密纹及多层真空烧结",
      es: "Tejido plano, asargado, holandés y laminados sinterizados",
      de: "Leinwand-, Köper-, Tressengewebe und Sinterlaminate",
      fr: "Tissages uni, croisé, hollandais et laminés frittés",
      pt: "Trama lisa, cruzada, holandesa e laminados sinterizados"
    }),
    val: "1 µm – 25 mm"
  }];
  const quickSpecStrip = [{
    label: pick(lang, {
      en: "Alloy Families",
      zh: "标准合金系列",
      es: "Familias de aleaciones",
      de: "Legierungsfamilien",
      fr: "Familles d'alliages",
      pt: "Famílias de ligas"
    }),
    val: "Austenitic, Duplex, Superalloys"
  }, {
    label: pick(lang, {
      en: "PREN Index Range",
      zh: "耐点蚀指数区间",
      es: "Rango de índice PREN",
      de: "PREN-Indexbereich",
      fr: "Plage d'indice PREN",
      pt: "Faixa de índice PREN"
    }),
    val: "PREN 19 – 68"
  }, {
    label: pick(lang, {
      en: "Tolerance Standards",
      zh: "国际制造公差标准",
      es: "Normas de tolerancia",
      de: "Toleranznormen",
      fr: "Normes de tolérances",
      pt: "Normas de tolerância"
    }),
    val: "ISO 9044 / ASTM E2016"
  }];
  const categoryFilters = [{
    id: "All",
    label: pick(lang, {
      en: "All",
      zh: "全部",
      es: "Todas",
      de: "Alle",
      fr: "Tous",
      pt: "Todas"
    })
  }, {
    id: "Austenitic",
    label: pick(lang, {
      en: "Austenitic",
      zh: "奥氏体",
      es: "Austenítico",
      de: "Austenitisch",
      fr: "Austénitique",
      pt: "Austenítico"
    })
  }, {
    id: "High-Alloy",
    label: pick(lang, {
      en: "High-Alloy",
      zh: "高合金",
      es: "Alta aleación",
      de: "Hochlegiert",
      fr: "Haut alliage",
      pt: "Alta liga"
    })
  }, {
    id: "Duplex",
    label: pick(lang, {
      en: "Duplex",
      zh: "双相钢",
      es: "Dúplex",
      de: "Duplex",
      fr: "Duplex",
      pt: "Duplex"
    })
  }, {
    id: "Superalloy",
    label: pick(lang, {
      en: "Superalloy",
      zh: "超合金",
      es: "Superaleación",
      de: "Superlegierung",
      fr: "Superalliage",
      pt: "Superliga"
    })
  }];
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const filteredAlloys = selectedCategory === "All" ? ALLOY_DATA : ALLOY_DATA.filter(a => a.category === selectedCategory);
  return <section id="alloyguide" data-nav="light" className="bg-background text-foreground mf-section border-b border-border transition-colors relative overflow-hidden">
      <div className="mf-container relative z-10">
        {/* Section Header */}
        <Reveal className="max-w-3xl mx-auto text-center space-y-4">
          <div className="flex items-center justify-center gap-2">
            <Badge variant="outline" className="px-3 py-1 font-mono mf-caption bg-card">
              <Microscope className="size-3.5 text-primary mr-1" aria-hidden="true" />
              <span>{pick(lang, copy.sec2Badge)}</span>
            </Badge>
          </div>

          <h2 className="mf-h2 text-foreground font-sans">
            {pick(lang, copy.sec2Heading)}
          </h2>

          <p className="mf-lead text-muted-foreground leading-relaxed">
            {pick(lang, copy.sec2Lead)}
          </p>
        </Reveal>

        {/* Main Content Tabs */}
        <div className="mt-12 md:mt-16">
          <TooltipProvider>
            <Tabs defaultValue="cards" className="w-full">
              {/* Top Controls Bar */}
              <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8 bg-card p-3 rounded-lg border border-border">
                {/* View Switcher Tabs */}
                <TabsList className="bg-muted p-1 rounded-lg">
                  <TabsTrigger value="cards" className="mf-caption md:text-sm px-4 py-2 font-medium">
                    <Layers className="size-4 mr-2" />
                    {pick(lang, copy.tabCards)}
                  </TabsTrigger>
                  <TabsTrigger value="matrix" className="mf-caption md:text-sm px-4 py-2 font-medium">
                    <FileText className="size-4 mr-2" />
                    {pick(lang, copy.tabMatrix)}
                  </TabsTrigger>
                </TabsList>

                {/* Category Filter Pills */}
                <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto pb-2 md:pb-0 scrollbar-none">
                  <span className="mf-caption text-muted-foreground font-medium mr-1 hidden lg:inline-flex items-center">
                    <SlidersHorizontal className="size-3 mr-1" /> {pick(lang, copy.categoryFilterLabel)}
                  </span>
                  {categoryFilters.map(cat => <Button key={cat.id} onClick={() => setSelectedCategory(cat.id)} variant={selectedCategory === cat.id ? "default" : "outline"} size="sm" className="mf-caption px-3 py-1.5 font-medium whitespace-nowrap transition-all">
                      {cat.label}
                    </Button>)}
                </div>
              </div>

              {/* TAB 1: Specification Cards View */}
              <TabsContent value="cards" className="space-y-6 focus-visible:outline-none">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {filteredAlloys.map((alloy, i) => <Reveal key={alloy.id} delay={Math.min(i * 60, 300)}>
                      <div data-index={i} className="bg-card text-card-foreground border border-border rounded-lg transition-all duration-300 flex flex-col justify-between overflow-hidden mf-hover-lift h-full">
                        {/* Card Header */}
                        <div className="p-6 border-b border-border bg-muted/20">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className="mf-caption font-mono border-border bg-background">
                                  {alloy.unsNumber}
                                </Badge>
                                <Badge variant="outline" className="bg-muted text-foreground mf-caption border border-border">
                                  {pick(lang, alloy.categoryI18n)}
                                </Badge>
                              </div>
                              <h3 className="mf-h3 text-foreground font-sans">
                                {alloy.grade}
                              </h3>
                            </div>

                            {/* PREN Badge */}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="cursor-help bg-background border border-border rounded-lg p-2 text-center min-w-[70px]">
                                  <span className="block mf-caption text-muted-foreground font-mono uppercase tracking-wider">
                                    PREN
                                  </span>
                                  <span className="text-lg font-bold font-mono text-primary">
                                    {alloy.prenScore}
                                  </span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p className="font-semibold mf-caption mb-1">
                                  {pick(lang, copy.prenTooltipTitle)}
                                </p>
                                <p className="mf-caption text-primary-foreground/90">
                                  {pick(lang, copy.prenTooltipDesc)}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </div>

                          {/* Quick Spec Meters */}
                          <div className="mt-5 pt-4 border-t border-border/60">
                            {/* Corrosion Rating */}
                            <div className="flex items-center gap-3">
                              <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary shrink-0">
                                <ShieldCheck className="size-5" aria-hidden="true" />
                              </div>
                              <div>
                                <span className="block mf-caption text-muted-foreground font-medium">
                                  {pick(lang, copy.corrosionRatingLabel)}
                                </span>
                                <div className="flex items-center gap-1 mt-0.5">
                                  <span className="text-sm font-semibold text-foreground mr-1">
                                    {pick(lang, alloy.corrosionRatingI18n)}
                                  </span>
                                  <div className="flex gap-0.5" title={`${alloy.corrosionLevel}/4 Rating`}>
                                    {[1, 2, 3, 4].map(step => <span key={step} className={`size-1.5 rounded-full ${step <= alloy.corrosionLevel ? "bg-primary" : "bg-muted-foreground/30"}`} />)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Card Body */}
                        <div className="p-6 space-y-5 flex-1">
                          {/* Key Features */}
                          <div>
                            <h4 className="mf-caption font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                              <CircleCheck className="size-3.5 text-primary" aria-hidden="true" />
                              <span>{pick(lang, copy.keyTraitsTitle)}</span>
                            </h4>
                            <ul className="space-y-1.5 mf-caption text-foreground/90">
                              {alloy.keyFeatures.map((feat, idx) => <li key={idx} className="flex items-start gap-2">
                                  <span className="size-1 rounded-full bg-primary mt-1.5 shrink-0" />
                                  <span>{pick(lang, feat)}</span>
                                </li>)}
                            </ul>
                          </div>

                          {/* Chemical Composition Bar */}
                          <div className="bg-muted/40 p-3 rounded-lg border border-border/80">
                            <span className="block mf-caption font-semibold text-muted-foreground mb-1.5">
                              {pick(lang, copy.nominalCompTitle)}
                            </span>
                            <div className="grid grid-cols-4 gap-2 text-center">
                              {alloy.composition.map((comp, idx) => <div key={idx} className="bg-background rounded p-1 border border-border/50">
                                  <span className="block mf-caption text-muted-foreground font-mono">
                                    {comp.element}
                                  </span>
                                  <span className="mf-caption font-bold font-mono text-foreground">
                                    {comp.percentage}
                                  </span>
                                </div>)}
                            </div>
                          </div>

                          {/* Recommended Weaves */}
                          <div>
                            <span className="block mf-caption font-semibold text-muted-foreground mb-1.5">
                              {pick(lang, copy.recommendedWeavesTitle)}
                            </span>
                            <div className="flex flex-wrap gap-1.5">
                              {alloy.recommendedWeaves.map((weave, idx) => <Badge key={idx} variant="outline" className="mf-caption bg-muted text-foreground border border-border">
                                  {pick(lang, weave)}
                                </Badge>)}
                            </div>
                          </div>

                          {/* Typical Applications */}
                          <div>
                            <span className="block mf-caption font-semibold text-muted-foreground mb-1.5">
                              {pick(lang, copy.applicationsTitle)}
                            </span>
                            <p className="mf-caption text-muted-foreground leading-normal">
                              {alloy.applications.map(app => pick(lang, app)).join(" • ")}
                            </p>
                          </div>
                        </div>

                        {/* Card Footer Actions */}
                        <div className="p-4 bg-muted/20 border-t border-border flex items-center justify-between gap-3">
                          <div className="mf-caption">
                            <span className="text-muted-foreground block mf-caption">
                              {pick(lang, copy.costIndexLabel)}
                            </span>
                            <span className="font-medium text-foreground font-mono">
                              {pick(lang, alloy.costProfileI18n)}
                            </span>
                          </div>

                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" className="mf-caption" asChild>
                              <Link to="/CorrosionResistanceMatricesPage">
                                {pick(lang, copy.btnGradeMatrix)}
                              </Link>
                            </Button>

                            <Link to="/RequestAQuotePage" className="inline-flex items-center text-primary hover:underline font-semibold mf-caption">
                              <span>{pick(lang, copy.btnInquireRfq)}</span>
                              <ArrowRight className="size-3.5 ml-1" aria-hidden="true" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </Reveal>)}
                </div>
              </TabsContent>

              {/* TAB 2: Comparison Matrix Table View */}
              <TabsContent value="matrix" className="focus-visible:outline-none">
                <Reveal>
                  <div className="bg-card text-card-foreground border border-border rounded-lg overflow-hidden">
                    <div className="p-4 bg-muted/30 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div>
                        <h3 className="text-base font-bold text-foreground">
                          {pick(lang, copy.matrixTableTitle)}
                        </h3>
                        <p className="mf-caption text-muted-foreground">
                          {pick(lang, copy.matrixTableSubtitle)}
                        </p>
                      </div>

                      <Badge variant="outline" className="w-fit mf-caption bg-background">
                        ISO & ASTM E2016 Compliant Standards
                      </Badge>
                    </div>

                    <div className="overflow-x-auto">
                      <Table className="min-w-[760px]">
                        <TableHeader className="bg-muted/50">
                          <TableRow>
                            <TableHead className="font-semibold text-foreground mf-caption min-w-[140px]">
                              {pick(lang, copy.thAlloyGrade)}
                            </TableHead>
                            <TableHead className="font-semibold text-foreground mf-caption">
                              {pick(lang, copy.thUnsNo)}
                            </TableHead>
                            <TableHead className="font-semibold text-foreground mf-caption text-center">
                              {pick(lang, copy.thPren)}
                            </TableHead>
                            <TableHead className="font-semibold text-foreground mf-caption">
                              {pick(lang, copy.thAcidRes)}
                            </TableHead>
                            <TableHead className="font-semibold text-foreground mf-caption">
                              {pick(lang, copy.thChlorideRes)}
                            </TableHead>
                            <TableHead className="font-semibold text-foreground mf-caption">
                              {pick(lang, copy.thRelCost)}
                            </TableHead>
                            <TableHead className="font-semibold text-foreground mf-caption text-right">
                              {pick(lang, copy.thSpec)}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredAlloys.map((alloy, i) => <TableRow key={alloy.id} data-index={i} className="hover:bg-muted/40 transition-colors">
                              <TableCell className="font-bold text-foreground mf-caption py-3">
                                <div className="flex flex-col">
                                  <span>{alloy.grade}</span>
                                  <span className="mf-caption text-muted-foreground font-normal">
                                    {pick(lang, alloy.categoryI18n)}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="font-mono mf-caption text-muted-foreground py-3">
                                {alloy.unsNumber}
                              </TableCell>
                              <TableCell className="text-center font-mono font-bold text-primary mf-caption py-3">
                                {alloy.prenScore}
                              </TableCell>
                              <TableCell className="mf-caption py-3">
                                <Badge variant="outline" className={`mf-caption ${alloy.acidResistance === "Immune" || alloy.acidResistance === "Superior" ? "bg-primary/10 border-primary/30 text-primary" : "bg-muted text-muted-foreground"}`}>
                                  {pick(lang, alloy.acidResistanceI18n)}
                                </Badge>
                              </TableCell>
                              <TableCell className="mf-caption py-3">
                                <Badge variant="outline" className={`mf-caption ${alloy.chloridePitting === "Extreme" || alloy.chloridePitting === "Exceptional" ? "bg-primary/10 border-primary/30 text-primary" : "bg-muted text-muted-foreground"}`}>
                                  {pick(lang, alloy.chloridePittingI18n)}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-mono mf-caption text-muted-foreground py-3">
                                {pick(lang, alloy.costProfileI18n)}
                              </TableCell>
                              <TableCell className="text-right py-3">
                                <Link to="/RequestAQuotePage" className="inline-flex items-center justify-end text-primary hover:underline font-medium mf-caption gap-1">
                                  <span>{pick(lang, copy.btnSpecs)}</span>
                                  <ArrowRight className="size-3" />
                                </Link>
                              </TableCell>
                            </TableRow>)}
                        </TableBody>
                      </Table>
                    </div>

                    <div className="p-4 bg-muted/20 border-t border-border flex flex-col md:flex-row items-center justify-between gap-3 mf-caption text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Info className="size-4 text-primary shrink-0" />
                        <span>
                          {pick(lang, copy.tableFootInfo)}
                        </span>
                      </div>

                      <Link to="/CorrosionResistanceMatricesPage" className="text-primary hover:underline font-semibold inline-flex items-center shrink-0">
                        <span>{pick(lang, copy.tableFootLink)}</span>
                        <ArrowRight className="size-3.5 ml-1" aria-hidden="true" />
                      </Link>
                    </div>
                  </div>
                </Reveal>
              </TabsContent>
            </Tabs>
          </TooltipProvider>
        </div>

        {/* Single mid-page prompt for the page */}
        <div className="mt-12">
          <InlineCta text={pick(lang, {
          en: "Need assistance determining alloy suitability for aggressive chemical or high-temperature media?",
          zh: "需要协助评估在强腐蚀化学介质或高温工况下的适宜金属丝网合金牌号？",
          es: "¿Necesita ayuda para determinar la idoneidad de la aleación en medios químicos agresivos o de alta temperatura?",
          de: "Benötigen Sie Unterstützung bei der Auswahl der passenden Legierung für aggressive chemische oder Heißgas-Medien?",
          fr: "Besoin d'aide pour choisir l'alliage approprié en milieux chimiques agressifs ou à haute température ?",
          pt: "Precisa de assistência para determinar a liga ideal para meios químicos agressivos ou de alta temperatura?"
        })} buttonLabel={pick(lang, COMMON.requestQuote)} to="/RequestAQuotePage" />
        </div>
      </div>
    </section>;
}